import { z } from 'zod';
import { ApprovalDecision, DraftChannel, DraftRoutineness } from './draft-approval.js';
/**
 * AgentAction — one row in the agent's action log (`/agent`). Distinct
 * from `AuditEvent` (forensic trail of every mutation; many actions per
 * minute) and from `AgentTodo` (the approval inbox; an action is what the
 * agent *did*, a todo is what it wants the CEO to look at).
 *
 * The agent log is the CEO-facing transparency surface: every draft the
 * agent wrote, every email it queued, every meeting it scheduled. The
 * row carries enough detail to audit + replay, but lives in its own
 * collection so the heavy `audit/` log doesn't have to surface in the
 * portal UI directly.
 *
 * Multi-tenant: every doc carries `orgId`. Server-only writes from the
 * agent runtime; clients only read.
 *
 * Naming note: the route is `/agent` (singular agent log); the schema is
 * `AgentAction` (one action per row). The collection is `agentActions`
 * (matches Firestore's plural-snake convention used elsewhere in the
 * repo: `agentTodos`, `externalSignals`).
 */
export const AGENT_ACTIONS_COLLECTION = 'agentActions';
/**
 * What the agent did. Kept short + verbal — the row renders the kind in
 * the same all-caps action column as `<AuditRow>`. Add new kinds as the
 * agent's repertoire grows.
 */
export const AgentActionKind = z.enum([
    /** Wrote a draft (email, message, doc) awaiting CEO approval. */
    'drafted',
    /** Sent a communication (email, Slack message) — actually went out. */
    'sent',
    /** Booked a calendar event or queued a task on someone's plate. */
    'scheduled',
    /** Set up monitoring on an entity / metric / channel. */
    'watched',
    /** Proposed an action via a todo for CEO review (no execution yet). */
    'proposed',
    /** Synthesized a summary or insight (linked via `srcInsightId`). */
    'synthesized',
    /** A shipping/connector failure or noteworthy degradation the CEO should see. */
    'errored',
]);
export const AgentActionStatus = z.enum([
    /** Default for actions the agent took autonomously. */
    'completed',
    /** The action is in flight (long-running synthesis, scheduled call). */
    'pending',
    /** The action failed and won't be retried. */
    'failed',
    /** The CEO superseded or cancelled the action. */
    'cancelled',
    /**
     * The agent prepared a draft for CEO sign-off but has not sent it yet.
     * Surfaces on `/approvals` (Phase 4, Epic #123). Distinct from `pending`
     * which covers actions the agent itself is still working on (synthesis
     * in flight, scheduled call). A draft can only leave this state via the
     * approvals routes (`POST /api/orgs/:o/approvals/:draftId`).
     */
    'draft',
    /**
     * The CEO explicitly rejected the draft on the approvals queue. The
     * agent will not send. Distinct from `cancelled` (which covers any
     * supersede / withdraw path) so the approvals audit replay can tell
     * "CEO said no" from "agent withdrew on its own".
     */
    'rejected',
]);
export const AgentAction = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** ISO timestamp the action took place / was queued. */
    at: z.string(),
    /**
     * UID stamped on the action. Conventional values:
     *   - `'agent'` — the agent runtime itself
     *   - `'system:cron'` — periodic synthesis (briefing headline, signals)
     *   - a real user uid — when an agent action was executed on behalf of
     *     a user (e.g. they hit Run on a draft and the agent fired the
     *     send through the connector)
     */
    actorUid: z.string().min(1),
    kind: AgentActionKind,
    /**
     * Display label for what the action concerned. Free-form — typically
     * an entity name ("Acme prep brief"), a verb-phrase ("watch #sales-eu
     * for quiet days"), or a citation slug ("draft to @taliab — re Q3
     * planning"). Renders inside `<EntityName>` on the surface.
     */
    target: z.string().min(1).max(200),
    /** Optional one-line meta (`"6 cites · model gemini-3.1-flash-lite-preview"`). */
    meta: z.string().max(400).optional(),
    /**
     * When this action came from an insight, the originating insight id.
     * Used to chain "agent surfaced X → drafted Y → CEO approved Z" in the
     * Today/Insights drill-in.
     */
    srcInsightId: z.string().optional(),
    /** Optional href into the agent action's source/result surface. */
    href: z.string().optional(),
    status: AgentActionStatus.default('completed'),
    /**
     * Approvals-queue fields. Populated when `status === 'draft'` so the
     * `/approvals` surface can render a row without a second collection
     * round-trip; the `decision` field is server-stamped on the first
     * `POST /api/orgs/:o/approvals/:draftId` and lets the audit replay
     * show "agent drafted → CEO {approved|edited|rejected}" against this
     * single row. All optional so existing rows (notably `kind: 'sent'`,
     * `'synthesized'`, etc.) round-trip without migration.
     */
    draftSubject: z.string().max(200).optional(),
    draftBody: z.string().max(8000).optional(),
    draftTo: z.array(z.string()).optional(),
    draftChannel: DraftChannel.optional(),
    draftRoutineness: DraftRoutineness.optional(),
    draftConfidence: z.number().min(0).max(1).optional(),
    /** CEO decision once recorded. Server-stamped; absent while `status === 'draft'`. */
    decision: ApprovalDecision.optional(),
    /** ISO timestamp the CEO recorded the decision. */
    decidedAt: z.string().optional(),
    /** UID that recorded the decision (echoes the auth context). */
    decidedBy: z.string().optional(),
    /** If `decision === 'edit'`, the CEO's edited body (stored alongside the original). */
    editedBody: z.string().max(8000).optional(),
    /** Optional rejection reason when `decision === 'reject'`. */
    rejectionReason: z.string().max(500).optional(),
});
/** Wire shape returned by `GET /api/orgs/:o/agent/actions`. */
export const ListAgentActionsResponse = z.object({
    actions: z.array(AgentAction),
});
